import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Dumbbell,
  Clock,
  Calendar,
  Target,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { patientApiService } from '../../services/api';
import { getEnvironmentConfig, getCurrentPlatform } from '../../config/platforms';

const ISO_DAY_NAMES: { [key: number]: string } = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday'
};

const FITNESS_LEVEL_LABELS: { [key: string]: string } = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced'
};

interface WorkoutPlanPreviewProps {
  /** 'plan' previews a workout_plan row by id (/plan/:id); 'routine' previews a base routine by key (/routine/:key). */
  kind?: 'plan' | 'routine';
}

export function WorkoutPlanPreview({ kind = 'plan' }: WorkoutPlanPreviewProps) {
  const params = useParams<{ id?: string; key?: string }>();
  const [searchParams] = useSearchParams();
  const isRoutine = kind === 'routine';
  const id = isRoutine ? params.key : params.id;
  const navigate = useNavigate();
  const platform = getCurrentPlatform();
  const config = getEnvironmentConfig();

  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set([1]));

  useEffect(() => {
    if (!id) return;
    const fetchPlan = async () => {
      try {
        setLoading(true);
        const data = isRoutine
          ? await patientApiService.getPublicBaseRoutine(id)
          : await patientApiService.getPublicWorkoutPlan(id);
        setPlan(data);
      } catch (err: any) {
        if (err?.status === 404 || err?.message?.includes('Not Found')) {
          setError('not_found');
        } else {
          setError('error');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [id, isRoutine]);

  // A routine link can carry the practice's referral code (/routine/upper?ref=CODE) so attribution survives signup.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (isRoutine && ref) {
      sessionStorage.setItem('pending_referral_code', ref.toLowerCase());
    }
  }, [isRoutine, searchParams]);

  const rememberChoice = () => {
    if (isRoutine) {
      sessionStorage.setItem('pending_routine_key', id!);
    } else {
      sessionStorage.setItem('pending_workout_plan_id', id!);
    }
  };

  const handleStartPlan = () => {
    rememberChoice();
    navigate('/signup');
  };

  const handleLogin = () => {
    rememberChoice();
    navigate('/login');
  };

  const toggleWeek = (weekNumber: number) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekNumber)) {
        next.delete(weekNumber);
      } else {
        next.add(weekNumber);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{isRoutine ? 'Routine Not Available' : 'Plan Not Found'}</h1>
          <p className="text-gray-500 mb-6">
            {isRoutine
              ? 'This routine is not available right now. You can still create an account and build your own plan.'
              : 'This workout plan may have been removed or the link may be incorrect.'}
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Create Your Own Plan
          </button>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h1>
          <p className="text-gray-500 mb-6">We couldn't load this workout plan. Please try again later.</p>
        </div>
      </div>
    );
  }

  const totalExercises = plan.weeks?.reduce((total: number, week: any) =>
    total + (week.schedules?.length || 0), 0) || 0;

  const sessionsPerWeek = plan.weeks?.[0]?.schedules
    ? new Set(plan.weeks[0].schedules.map((s: any) => s.day_of_week)).size
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <Dumbbell className="h-6 w-6 text-primary" />
            <span className="text-lg font-semibold text-primary">{config.appName || platform.name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{plan.name || 'Workout Plan'}</h1>
          {plan.description && (
            <p className="text-lg text-gray-600 mb-6">{plan.description}</p>
          )}
          <div className="flex flex-wrap gap-4">
            {plan.duration_weeks && (
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded-full px-4 py-2 shadow-sm">
                <Calendar className="h-4 w-4 text-primary" />
                <span>{plan.duration_weeks} {plan.duration_weeks === 1 ? 'week' : 'weeks'}</span>
              </div>
            )}
            {sessionsPerWeek > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded-full px-4 py-2 shadow-sm">
                <Target className="h-4 w-4 text-primary" />
                <span>{sessionsPerWeek}x per week</span>
              </div>
            )}
            {plan.fitness_level && (
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded-full px-4 py-2 shadow-sm">
                <Dumbbell className="h-4 w-4 text-primary" />
                <span>{FITNESS_LEVEL_LABELS[plan.fitness_level] || plan.fitness_level}</span>
              </div>
            )}
            {!isRoutine && plan.plan_type && (
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded-full px-4 py-2 shadow-sm">
                <Target className="h-4 w-4 text-primary" />
                <span>{plan.plan_type}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Week Schedule */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Weekly Schedule</h2>
        <div className="space-y-4">
          {plan.weeks?.map((week: any) => (
            <div key={week.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleWeek(week.week_number)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{isRoutine ? 'Each week' : `Week ${week.week_number}`}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {week.schedules?.length || 0} sessions
                  </span>
                  {expandedWeeks.has(week.week_number) ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {expandedWeeks.has(week.week_number) && week.schedules && (
                <div className="border-t border-gray-100 px-5 py-3 space-y-3">
                  {week.schedules.map((schedule: any, idx: number) => (
                    <div key={schedule.id || idx} className="flex items-start gap-3 py-2">
                      <div className="flex-shrink-0 w-20">
                        <span className="text-xs font-medium text-primary uppercase">
                          {ISO_DAY_NAMES[schedule.day_of_week] || `Day ${schedule.day_of_week}`}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        {schedule.exercise_prescription ? (
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              {schedule.exercise_prescription.name}
                            </p>
                            <div className="flex flex-wrap gap-3 mt-1">
                              {schedule.exercise_prescription.workout_type && (
                                <span className="text-xs text-gray-500">
                                  {schedule.exercise_prescription.workout_type}
                                </span>
                              )}
                              {schedule.exercise_prescription.estimated_duration_minutes && (
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {schedule.exercise_prescription.estimated_duration_minutes} min
                                </span>
                              )}
                              {schedule.exercise_prescription.fitness_level && (
                                <span className="text-xs text-gray-500">
                                  {FITNESS_LEVEL_LABELS[schedule.exercise_prescription.fitness_level] || schedule.exercise_prescription.fitness_level}
                                </span>
                              )}
                            </div>
                            {schedule.exercise_prescription.modules?.length > 0 && (
                              <div className="mt-3 space-y-3">
                                {schedule.exercise_prescription.modules.map((mod: any, modIdx: number) => (
                                  <div key={modIdx}>
                                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                                      {mod.name || mod.module_name}
                                      {mod.duration_minutes && <span className="font-normal text-gray-400 ml-1">({mod.duration_minutes} min)</span>}
                                    </p>
                                    {mod.exercises?.length > 0 && (
                                      <div className="space-y-2">
                                        {mod.exercises.map((ex: any, exIdx: number) => (
                                          <div key={exIdx} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
                                            {(ex.image_url || ex.exercise_gif) && (
                                              <img
                                                src={ex.image_url || ex.exercise_gif}
                                                alt={ex.official_title || ex.display_name}
                                                className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                                              />
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium text-gray-900 truncate">
                                                {ex.official_title || ex.display_name || 'Exercise'}
                                              </p>
                                              <div className="flex flex-wrap gap-2 mt-0.5">
                                                {ex.parameters?.sets && (
                                                  <span className="text-xs text-gray-500">{ex.parameters.sets} sets</span>
                                                )}
                                                {ex.parameters?.reps && (
                                                  <span className="text-xs text-gray-500">{ex.parameters.reps} reps</span>
                                                )}
                                                {ex.parameters?.rest_time && (
                                                  <span className="text-xs text-gray-500">{ex.parameters.rest_time}s rest</span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">Session details pending</p>
                        )}
                      </div>
                      {schedule.is_optional && (
                        <span className="flex-shrink-0 text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">
                          Optional
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <button
            onClick={handleStartPlan}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold text-lg hover:opacity-90 transition-opacity"
          >
            {isRoutine ? 'Start This Routine' : 'Start This Plan'}
            <ArrowRight className="h-5 w-5" />
          </button>
          <p className="text-center mt-3 text-sm text-gray-500">
            Already have an account?{' '}
            <button onClick={handleLogin} className="text-primary font-medium hover:underline">
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}